/**
 * Creator Discovery Service
 * Handles searching and discovering creators/influencers in the marketplace
 */

import { supabase } from '../supabaseClient';

export interface Creator {
  id: string;
  user_id: string;
  store_name: string;
  store_description: string;
  profile_image?: string;
  banner_image?: string;
  bio?: string;
  social_links?: {
    instagram?: string;
    tiktok?: string;
    youtube?: string;
  };
  total_videos?: number;
  total_followers?: number;
  avg_engagement_rate?: number;
  joined_at: string;
}

export interface CreatorSearchFilters {
  searchQuery?: string;
  keywords?: string[];
  minFollowers?: number;
  minEngagement?: number;
  sortBy?: 'followers' | 'engagement' | 'newest';
}

/**
 * Search for creators with filters
 * @param filters - Search and filter options
 * @param limit - Number of results to return (default: 20)
 * @param offset - Offset for pagination (default: 0)
 */
export async function searchCreators(
  filters: CreatorSearchFilters = {},
  limit: number = 20,
  offset: number = 0
): Promise<{ data: Creator[] | null; error: any }> {
  try {
    let query = supabase
      .from('seller_profile_public')
      .select('*');

    // Apply search query on store_name or bio
    if (filters.searchQuery) {
      query = query.or(`store_name.ilike.%${filters.searchQuery}%,bio.ilike.%${filters.searchQuery}%`);
    }

    // Apply keyword search (search_keywords in seller_store)
    // NOTE: We need to join with seller_store table to access search_keywords
    // This will be handled by fetching seller_store separately if keywords filter is provided

    // Apply follower filter
    if (filters.minFollowers !== undefined) {
      query = query.gte('total_followers', filters.minFollowers);
    }

    // Apply engagement rate filter
    if (filters.minEngagement !== undefined) {
      query = query.gte('avg_engagement_rate', filters.minEngagement);
    }

    // Apply sorting
    switch (filters.sortBy) {
      case 'followers':
        query = query.order('total_followers', { ascending: false, nullsFirst: false });
        break;
      case 'engagement':
        query = query.order('avg_engagement_rate', { ascending: false, nullsFirst: false });
        break;
      case 'newest':
        query = query.order('joined_at', { ascending: false });
        break;
      default:
        query = query.order('total_followers', { ascending: false, nullsFirst: false });
    }

    // Apply pagination
    query = query.range(offset, offset + limit - 1);

    const { data: creators, error: creatorsError } = await query;

    if (creatorsError) {
      console.error('Error searching creators:', creatorsError);
      return { data: null, error: creatorsError };
    }

    // If keywords filter is provided, fetch seller_store and filter by keywords
    if (filters.keywords && filters.keywords.length > 0 && creators) {
      const sellerIds = creators.map((c) => c.id);

      const { data: stores, error: storesError } = await supabase
        .from('seller_store')
        .select('seller_id, search_keywords')
        .in('seller_id', sellerIds);

      if (storesError) {
        console.error('Error fetching seller stores:', storesError);
        // Continue without keyword filtering
        return { data: creators, error: null };
      }

      // Filter creators whose search_keywords contain at least one of the filter keywords
      const storesMap = new Map(
        (stores || []).map((s) => [s.seller_id, s.search_keywords || []])
      );

      const filteredCreators = creators.filter((creator) => {
        const creatorKeywords = storesMap.get(creator.id) || [];
        return filters.keywords!.some((keyword) =>
          creatorKeywords.some((ck: string) =>
            ck.toLowerCase().includes(keyword.toLowerCase())
          )
        );
      });

      return { data: filteredCreators, error: null };
    }

    return { data: creators || [], error: null };
  } catch (err) {
    console.error('Unexpected error in searchCreators:', err);
    return { data: null, error: err };
  }
}

/**
 * Get a single creator by ID
 * @param creatorId - The creator's seller_profile ID
 */
export async function getCreatorById(
  creatorId: string
): Promise<{ data: Creator | null; error: any }> {
  try {
    const { data: creator, error: creatorError } = await supabase
      .from('seller_profile_public')
      .select('*')
      .eq('id', creatorId)
      .single();

    if (creatorError) {
      console.error('Error fetching creator:', creatorError);
      return { data: null, error: creatorError };
    }

    return { data: creator, error: null };
  } catch (err) {
    console.error('Unexpected error in getCreatorById:', err);
    return { data: null, error: err };
  }
}

/**
 * Get trending creators (sorted by engagement rate and follower count)
 * @param limit - Number of trending creators to fetch (default: 10)
 */
export async function getTrendingCreators(
  limit: number = 10
): Promise<{ data: Creator[] | null; error: any }> {
  try {
    // Fetch creators with highest engagement and followers
    const { data: creators, error: creatorsError } = await supabase
      .from('seller_profile_public')
      .select('*')
      .order('avg_engagement_rate', { ascending: false, nullsFirst: false })
      .order('total_followers', { ascending: false, nullsFirst: false })
      .limit(limit);

    if (creatorsError) {
      console.error('Error fetching trending creators:', creatorsError);
      return { data: null, error: creatorsError };
    }

    return { data: creators || [], error: null };
  } catch (err) {
    console.error('Unexpected error in getTrendingCreators:', err);
    return { data: null, error: err };
  }
}

/**
 * Get creator's search keywords from seller_store
 * @param creatorId - The creator's seller_profile ID
 */
export async function getCreatorKeywords(
  creatorId: string
): Promise<{ data: string[] | null; error: any }> {
  try {
    const { data: store, error: storeError } = await supabase
      .from('seller_store')
      .select('search_keywords')
      .eq('seller_id', creatorId)
      .single();

    if (storeError) {
      console.error('Error fetching creator keywords:', storeError);
      return { data: null, error: storeError };
    }

    return { data: store?.search_keywords || [], error: null };
  } catch (err) {
    console.error('Unexpected error in getCreatorKeywords:', err);
    return { data: null, error: err };
  }
}
